/**
 * A pré-visualização do Desk Cloud (docs/spec/desk-cloud.md): em tela cheia, com anterior e próximo
 * (← e →, ou deslizar no celular), a faixa das miniaturas embaixo e Esc para fechar. A imagem aparece
 * grande; o áudio toca só para quem abriu; o PDF abre no visualizador do navegador. O botão principal
 * é o "Usar na mesa" do tipo.
 */
import { useEffect, useRef } from 'react';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import { serverUrl } from '../serverUrl.js';
import type { DeskFile } from '../shared.js';
import { Button } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { downloadFiles } from './cloudActions.js';
import { KIND_COLORS, actionsFor, durationText, roomTargets, splitName } from './cloudModel.js';
import { useCloud } from './cloudStore.js';
import { useCloudUi } from './cloudUi.js';
import { applyInRoom } from './inRoom.js';
import { useListen } from './listen.js';

/** Quanto o dedo anda para o lado para passar de arquivo. */
const SWIPE_PX = 60;

export function CloudPreview({ files, fileId, onPick, onClose }: { files: readonly DeskFile[]; fileId: string; onPick: (id: string) => void; onClose: () => void }) {
  const t = useT();
  const ui = useCloudUi();
  const listen = useListen();
  const rooms = useCloud((s) => s.summary?.rooms) ?? [];
  const strip = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const index = files.findIndex((file) => file.id === fileId);
  const file = files[index];

  const step = (delta: number) => {
    if (!files.length) return;
    const next = files[(Math.max(0, index) + delta + files.length) % files.length]!;
    onPick(next.id);
    useCloud.getState().setFocus(next.id);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  // a miniatura do arquivo aberto fica à vista na faixa
  useEffect(() => {
    strip.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [fileId]);

  // o áudio da prévia para quando ela fecha
  useEffect(() => () => useListen.getState().stop(), []);

  if (!file) return null;
  const { ext } = splitName(file.name);
  const target = roomTargets(file, rooms, ui.roomName)[0];
  const primary = target ? actionsFor(file, target.gm)[0] : undefined;
  const playing = listen.id === file.id && listen.playing;
  const url = serverUrl(file.url);
  const meta = [formatBytes(file.size), file.width && file.height ? t('cloud.details.pixels', { w: file.width, h: file.height }) : null, file.durationMs !== null ? durationText(file.durationMs) : null].filter(Boolean).join(' · ');

  return (
    <div className="rd-cloudPreview" role="dialog" aria-modal="true" aria-label={t('cloud.preview', { name: file.name })}>
      <div className="rd-cloudPreview__bar">
        <span className="rd-cloudPreview__kind">
          <span className="rd-cloudDot" style={{ background: KIND_COLORS[file.kind] }} aria-hidden="true" />
          {t(`cloud.kind.${file.kind}`)}
        </span>
        <div className="rd-cloudPreview__title">
          <span className="rd-cloudPreview__name">{file.name}</span>
          <span className="rd-cloudPreview__meta">{meta}</span>
        </div>
        <span className="rd-cloudPreview__pos">
          {index + 1} / {files.length}
        </span>
        <Button variant="secondary" icon="download" onClick={() => void downloadFiles([file])}>
          {ui.mobile ? '' : t('cloud.menu.download')}
        </Button>
        {primary && target && file.trashedAt === null ? (
          <Button
            variant="action"
            onClick={() => {
              onClose();
              void applyInRoom(file, primary, target.id);
            }}
          >
            {t(`cloud.use.${primary}`)}
          </Button>
        ) : null}
        <button type="button" className="rd-iconBtn" aria-label={t('cloud.preview.close')} onClick={onClose}>
          <Icon name="close" size={20} />
        </button>
      </div>
      <div
        className="rd-cloudPreview__stage"
        onPointerDown={(e) => {
          if (e.pointerType !== 'mouse') swipe.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={(e) => {
          const from = swipe.current;
          swipe.current = null;
          if (!from) return;
          const dx = e.clientX - from.x;
          if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(e.clientY - from.y)) step(dx < 0 ? 1 : -1);
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {files.length > 1 ? (
          <button type="button" className="rd-iconBtn rd-cloudPreview__nav is-prev" aria-label={t('cloud.preview.prev')} onClick={() => step(-1)}>
            <Icon name="chevronLeft" size={26} />
          </button>
        ) : null}
        {file.mime.startsWith('image/') ? (
          <img key={file.id} className="rd-cloudPreview__image" src={url} alt={file.name} />
        ) : file.mime.startsWith('audio/') || file.kind === 'audio' ? (
          <div className="rd-cloudPreview__audio">
            <button type="button" className={`rd-cloudPreview__play${playing ? ' is-playing' : ''}`} aria-label={t(playing ? 'cloud.pause' : 'cloud.listen', { name: file.name })} onClick={() => listen.toggle(file)}>
              <Icon name={playing ? 'pause' : 'play'} size={30} filled />
            </button>
            <span className="rd-cloudPreview__track">
              <span style={{ width: `${listen.id === file.id ? listen.progress * 100 : 0}%` }} />
            </span>
            <span className="rd-cloudPreview__only">{t('cloud.preview.onlyYou')}</span>
          </div>
        ) : file.mime === 'application/pdf' ? (
          <iframe key={file.id} className="rd-cloudPreview__pdf" src={url} title={file.name} />
        ) : file.mime.startsWith('video/') ? (
          <video key={file.id} className="rd-cloudPreview__image" src={url} controls />
        ) : (
          <div className="rd-cloudPreview__none">
            <Icon name="file" size={52} strokeWidth={1.2} />
            <span>{t('cloud.preview.none', { ext: ext || '?' })}</span>
          </div>
        )}
        {files.length > 1 ? (
          <button type="button" className="rd-iconBtn rd-cloudPreview__nav is-next" aria-label={t('cloud.preview.next')} onClick={() => step(1)}>
            <Icon name="chevronRight" size={26} />
          </button>
        ) : null}
      </div>
      {files.length > 1 ? (
        <div ref={strip} className="rd-cloudPreview__strip" role="group" aria-label={t('cloud.preview.strip')}>
          {files.map((other) => {
            const src = other.thumbUrl ?? (other.mime.startsWith('image/') ? other.url : null);
            return (
              <button key={other.id} type="button" className="rd-cloudPreview__thumb" aria-label={other.name} aria-current={other.id === file.id} onClick={() => onPick(other.id)}>
                {src ? <img src={serverUrl(src)} alt="" loading="lazy" /> : <Icon name={other.kind === 'audio' ? 'music' : 'file'} size={18} />}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
